import { ETAPES, type Etape } from './etapes.js';
import { TAILLE_CIBLE, cibleDe, foyersPresents, nombreHotesNecessaire, nombreParticipants, plageDe, previsionTables, taillePresente, type Cible, type PrevisionTables } from './foyers.js';
import { creerAleatoire, type Graine } from './prng.js';
import type { AffectationHote, Foyer } from './types.js';

export interface PropositionHotes {
  hotes: AffectationHote[];
  /** Volontaires présents non placés (ils seront convives aux trois étapes). */
  nonUtilises: string[];
  hotesIdeal: number;
}

/**
 * Proposition automatique d'hôtes (écran 4, « Nouvelle proposition »).
 * Règles 4 et 5 : un foyer reçoit à une seule étape ; chaque étape a ≥ 1 hôte ;
 * nombre idéal ≈ participants ÷ 4. On sert d'abord l'étape la moins pourvue, en
 * prenant le volontaire le moins flexible (le moins de disponibilités) ; la graine
 * ne départage que les ex æquo, pour varier la proposition sans la dégrader.
 */
export function proposerHotes(foyers: Foyer[], graine: Graine = 0, cible: Cible = TAILLE_CIBLE): PropositionHotes {
  const alea = creerAleatoire(graine);
  const presents = foyersPresents(foyers);
  const participants = nombreParticipants(foyers);
  const volontaires = alea.melanger(presents.filter((f) => f.veutRecevoir && f.disponibilites.length > 0));
  const hotesIdeal = nombreHotesNecessaire(participants, cible, volontaires);
  const affectes = new Map<string, Etape>();
  const compte: Record<Etape, number> = { entree: 0, plat: 0, dessert: 0 };
  const sature = new Set<Etape>();
  // Une étape a encore besoin d'hôtes tant que le compte idéal n'est pas atteint, ou que les
  // capacités déclarées de ses hôtes ne couvrent pas tous les participants.
  const besoin = (e: Etape): boolean => {
    if (compte[e] < hotesIdeal) return true;
    const places = volontaires.filter((f) => affectes.get(f.id) === e).reduce((s, f) => s + (f.capaciteMax ?? Number.POSITIVE_INFINITY), 0);
    return places < participants;
  };

  for (;;) {
    const ouvertes = ETAPES.filter((e) => !sature.has(e) && besoin(e));
    if (ouvertes.length === 0) break;
    // Étape la moins pourvue ; à égalité, celle qui a le moins de candidats restants.
    const candidatsDe = (e: Etape) => volontaires.filter((f) => !affectes.has(f.id) && f.disponibilites.includes(e));
    const etape = ouvertes.sort((a, b) => compte[a] - compte[b] || candidatsDe(a).length - candidatsDe(b).length || ETAPES.indexOf(a) - ETAPES.indexOf(b))[0] as Etape;
    const candidats = candidatsDe(etape);
    if (candidats.length === 0) {
      sature.add(etape);
      continue;
    }
    // Le moins flexible d'abord (ordre aléatoire de la graine pour les ex æquo).
    const choisi = candidats.sort((a, b) => a.disponibilites.length - b.disponibilites.length)[0] as Foyer;
    affectes.set(choisi.id, etape);
    compte[etape]++;
  }

  return {
    hotes: [...affectes].map(([foyerId, etape]) => ({ foyerId, etape })),
    nonUtilises: volontaires.filter((f) => !affectes.has(f.id)).map((f) => f.id),
    hotesIdeal,
  };
}

/** Taille de groupe attendue à une étape : participants ÷ hôtes, arrondie au-dessus (règle 6). */
export function tailleGroupeAttendue(participants: number, nbHotes: number): number {
  return nbHotes === 0 ? participants : Math.ceil(participants / nbHotes);
}

/**
 * Conseil affiché sous chaque colonne (écran 4) : « Dans la plage. Un 5e hôte ramènerait
 * les groupes à 4. », etc. `reserve` = volontaires disponibles pour cette étape et non placés.
 */
export function conseilEtape(participants: number, nbHotes: number, reserve: Foyer[], cibleOuPlage: Cible = TAILLE_CIBLE, prevision?: PrevisionTables): string {
  const plage = plageDe(cibleOuPlage);
  const cible = cibleDe(cibleOuPlage);
  const taille = prevision ? prevision.max : tailleGroupeAttendue(participants, nbHotes);
  if (nbHotes === 0) return 'Il faut au moins 1 hôte.';
  if (prevision && prevision.manque > 0) return `Capacités insuffisantes : ${prevision.manque} personne${prevision.manque > 1 ? 's' : ''} sans place. Ajoutez un hôte.`;
  const avecUnDePlus = tailleGroupeAttendue(participants, nbHotes + 1);
  const dansPlage = prevision ? prevision.min >= plage.min && prevision.max <= plage.max : taille >= plage.min && taille <= plage.max;
  const texteTaille = prevision && prevision.min !== prevision.max ? `${prevision.min} à ${prevision.max} par table` : `${taille} par table`;
  const prefixe = dansPlage ? 'Dans la plage.' : taille > plage.max ? `Hors plage : ${texteTaille}, c'est beaucoup.` : `Hors plage : ${texteTaille}, c'est peu.`;
  const ordinal = `${nbHotes + 1}e`;
  if (reserve.length === 0) {
    if (taille > cible && !dansPlage) return `${prefixe} Aucun autre volontaire disponible : sollicitez une voiture.`;
    return `${prefixe} Aucun autre volontaire disponible.`;
  }
  const premier = reserve[0] as Foyer;
  if (taille > cible) {
    return reserve.length === 1 ? `${prefixe} ${premier.nom} pourrait être le ${ordinal} hôte.` : `${prefixe} Un ${ordinal} hôte ramènerait les groupes à ${avecUnDePlus}.`;
  }
  return `${prefixe} ${reserve.length} volontaire${reserve.length > 1 ? 's' : ''} de réserve.`;
}

/** Prévision des tables d'une étape à partir des hôtes placés (foyers), hôtes compris. */
export function previsionEtape(participants: number, hotes: Foyer[]): PrevisionTables {
  return previsionTables(participants, hotes);
}

/** Nombre de personnes présentes chez un hôte (pour « 2 pers. »). */
export function tailleHote(f: Foyer): number {
  return taillePresente(f);
}
